import type { Components, Theme } from '@mui/material/styles'
import { createTheme as createMuiTheme } from '@mui/material/styles'
import { colorChannel } from '@mui/system'
import { components } from './core/components'
import { customShadows } from './core/custom-shadows'
import { mixins } from './core/mixins'
import { palette } from './core/palette'
import { shadows } from './core/shadows'
import { typography } from './core/typography'
import { themeConfig } from './theme-config'
import type { ThemeOptions } from './types'

// ----------------------------------------------------------------------

export const baseTheme: ThemeOptions = {
  colorSchemes: {
    light: {
      palette: palette.light,
      shadows: shadows.light,
      customShadows: customShadows.light,
    },
  },
  mixins,
  components: {
    MuiCollapse: {
      defaultProps: {
        unmountOnExit: true,
      },
    },
    ...components,
  },
  typography,
  shape: { borderRadius: 8 },
  direction: themeConfig.direction,
  cssVariables: themeConfig.cssVariables,
  defaultColorScheme: themeConfig.defaultMode,
}

// ----------------------------------------------------------------------

export const operator = {
  theme: {
    primaryLighter: '#EEF6EC',
    primaryLight: '#B3CCB2',
    primaryMain: '#435545',
    primaryDark: '#213D29',
    primaryDarker: '#0C281A',
    primaryContrastText: '#FFFFFF',
    secondaryLighter: '#EFD6FF',
    secondaryLight: '#C684FF',
    secondaryMain: '#8E33FF',
    secondaryDark: '#5119B7',
    secondaryDarker: '#27097A',
    secondaryContrastText: '#FFFFFF',
    infoLighter: '#CAFDF5',
    infoLight: '#61F3F3',
    infoMain: '#00B8D9',
    infoDark: '#006C9C',
    infoDarker: '#003768',
    infoContrastText: '#FFFFFF',
    successLighter: '#D3FCD2',
    successLight: '#77ED8B',
    successMain: '#22C55E',
    successDark: '#118D57',
    successDarker: '#065E49',
    successContrastText: '#ffffff',
    warningLighter: '#FFF5CC',
    warningLight: '#FFD666',
    warningMain: '#FFAB00',
    warningDark: '#B76E00',
    warningDarker: '#7A4100',
    warningContrastText: '#1C252E',
    errorLighter: '#FFE9D5',
    errorLight: '#FFAC82',
    errorMain: '#FF5630',
    errorDark: '#B71D18',
    errorDarker: '#7A0916',
    errorContrastText: '#FFFFFF',
  },
}

export function createTheme(): Theme {
  // Update core theme settings
  const updatedCore = baseTheme

  updatedCore.colorSchemes = {
    light: {
      palette: {
        ...palette.light,
        primary: {
          ...palette.light!.primary!,
          lighter: operator.theme.primaryLighter,
          lighterChannel: colorChannel(operator.theme.primaryLighter),
          light: operator.theme.primaryLight,
          lightChannel: colorChannel(operator.theme.primaryLight),
          main: operator.theme.primaryMain,
          mainChannel: colorChannel(operator.theme.primaryMain),
          dark: operator.theme.primaryDark,
          darkChannel: colorChannel(operator.theme.primaryDark),
          darker: operator.theme.primaryDarker,
          darkerChannel: colorChannel(operator.theme.primaryDarker),
          contrastText: operator.theme.primaryContrastText,
          contrastTextChannel: colorChannel(operator.theme.primaryContrastText),
        },
        secondary: {
          ...palette.light!.secondary!,
          lighter: operator.theme.secondaryLighter,
          lighterChannel: colorChannel(operator.theme.secondaryLighter),
          light: operator.theme.secondaryLight,
          lightChannel: colorChannel(operator.theme.secondaryLight),
          main: operator.theme.secondaryMain,
          mainChannel: colorChannel(operator.theme.secondaryMain),
          dark: operator.theme.secondaryDark,
          darkChannel: colorChannel(operator.theme.secondaryDark),
          darker: operator.theme.secondaryDarker,
          darkerChannel: colorChannel(operator.theme.secondaryDarker),
          contrastText: operator.theme.secondaryContrastText,
          contrastTextChannel: colorChannel(operator.theme.secondaryContrastText),
        },
        info: {
          ...palette.light!.info!,
          lighter: operator.theme.infoLighter,
          lighterChannel: colorChannel(operator.theme.infoLighter),
          light: operator.theme.infoLight,
          lightChannel: colorChannel(operator.theme.infoLight),
          main: operator.theme.infoMain,
          mainChannel: colorChannel(operator.theme.infoMain),
          dark: operator.theme.infoDark,
          darkChannel: colorChannel(operator.theme.infoDark),
          darker: operator.theme.infoDarker,
          darkerChannel: colorChannel(operator.theme.infoDarker),
          contrastText: operator.theme.infoContrastText,
          contrastTextChannel: colorChannel(operator.theme.infoContrastText),
        },
        success: {
          ...palette.light!.success!,
          lighter: operator.theme.successLighter,
          lighterChannel: colorChannel(operator.theme.successLighter),
          light: operator.theme.successLight,
          lightChannel: colorChannel(operator.theme.successLight),
          main: operator.theme.successMain,
          mainChannel: colorChannel(operator.theme.successMain),
          dark: operator.theme.successDark,
          darkChannel: colorChannel(operator.theme.successDark),
          darker: operator.theme.successDarker,
          darkerChannel: colorChannel(operator.theme.successDarker),
          contrastText: operator.theme.successContrastText,
          contrastTextChannel: colorChannel(operator.theme.successContrastText),
        },
        warning: {
          ...palette.light!.warning!,
          lighter: operator.theme.warningLighter,
          lighterChannel: colorChannel(operator.theme.warningLighter),
          light: operator.theme.warningLight,
          lightChannel: colorChannel(operator.theme.warningLight),
          main: operator.theme.warningMain,
          mainChannel: colorChannel(operator.theme.warningMain),
          dark: operator.theme.warningDark,
          darkChannel: colorChannel(operator.theme.warningDark),
          darker: operator.theme.warningDarker,
          darkerChannel: colorChannel(operator.theme.warningDarker),
          contrastText: operator.theme.warningContrastText,
          contrastTextChannel: colorChannel(operator.theme.warningContrastText),
        },
        error: {
          ...palette.light!.error!,
          lighter: operator.theme.errorLighter,
          lighterChannel: colorChannel(operator.theme.errorLighter),
          light: operator.theme.errorLight,
          lightChannel: colorChannel(operator.theme.errorLight),
          main: operator.theme.errorMain,
          mainChannel: colorChannel(operator.theme.errorMain),
          dark: operator.theme.errorDark,
          darkChannel: colorChannel(operator.theme.errorDark),
          darker: operator.theme.errorDarker,
          darkerChannel: colorChannel(operator.theme.errorDarker),
          contrastText: operator.theme.errorContrastText,
          contrastTextChannel: colorChannel(operator.theme.errorContrastText),
        },
      },
      shadows: shadows.light,
      customShadows: customShadows.light,
    },
  }

  // Update component settings
  const updatedComponents = {}

  // Create and return the final theme
  const theme = createMuiTheme(updatedCore, updatedComponents, {}, {})

  return theme
}
